// Request body shapes for /me/files + /me/folders + /me/uploads.
import { z } from 'zod';
import { FilePartition, FsName, FsTags } from './folder.js';
import { Md5, MAX_FILE_BYTES } from './file.js';
import { SameNameStrategy } from './upload.js';
export const GroupScopeInput = z.object({
    groupId: z.string().uuid(),
    availableAt: z.string().datetime({ offset: true }).nullable().optional(),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    allowParentAccess: z.boolean().optional(),
    pendingOpen: z.boolean().optional(),
});
export const ReplaceGroupScopesBody = z.object({
    groupScopes: z.array(GroupScopeInput).max(32),
});
export const GroupScopeBulkTargetKind = z.enum(['file', 'folder']);
export const BulkGroupScopeUpdate = z.object({
    kind: GroupScopeBulkTargetKind,
    id: z.string().uuid(),
    availableAt: z.string().datetime({ offset: true }).nullable().optional(),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    allowParentAccess: z.boolean().optional(),
    pendingOpen: z.boolean().optional(),
});
export const BulkGroupScopesBody = z.object({
    groupId: z.string().uuid(),
    updates: z.array(BulkGroupScopeUpdate).min(1).max(500),
});
export const BulkGroupScopeResult = z.object({
    kind: GroupScopeBulkTargetKind,
    id: z.string().uuid(),
    ok: z.boolean(),
    errorCode: z.string().nullable(),
});
export const BulkGroupScopesResponse = z.object({
    results: z.array(BulkGroupScopeResult),
    okCount: z.number().int().nonnegative(),
    failCount: z.number().int().nonnegative(),
});
// ---- folders ----------------------------------------------------------
export const CreateFolderBody = z.object({
    partition: FilePartition,
    /** NULL ⇒ create at the partition root. */
    parentId: z.string().uuid().nullable().optional(),
    name: FsName,
    description: z.string().max(2000).nullable().optional(),
    tags: FsTags.optional(),
    groupScopes: z.array(GroupScopeInput).max(32).optional(),
});
export const UpdateFolderBody = z.object({
    name: FsName.optional(),
    description: z.string().max(2000).nullable().optional(),
    tags: FsTags.optional(),
    /** Move under a different parent. NULL ⇒ root. */
    parentId: z.string().uuid().nullable().optional(),
    groupScopes: z.array(GroupScopeInput).max(32).optional(),
});
// ---- files ------------------------------------------------------------
export const UpdateFileBody = z.object({
    name: FsName.optional(),
    description: z.string().max(2000).nullable().optional(),
    tags: FsTags.optional(),
    folderId: z.string().uuid().nullable().optional(),
    groupScopes: z.array(GroupScopeInput).max(32).optional(),
});
export const CopyFileBody = z.object({
    /** New filename. Defaults to `<source>-copy<ext>` when omitted. */
    name: FsName.optional(),
    /** Destination folder; defaults to the same folder as the source. */
    targetFolderId: z.string().uuid().nullable().optional(),
});
// ---- bulk -------------------------------------------------------------
export const BulkAction = z.enum(['delete', 'restore', 'purge', 'move', 'tag-add', 'tag-remove']);
export const BulkFilesBody = z.object({
    fileIds: z.array(z.string().uuid()).min(1).max(500),
    action: BulkAction,
    /** Required for `move`. */
    targetFolderId: z.string().uuid().nullable().optional(),
    /** Required for `tag-add` / `tag-remove`. */
    tags: FsTags.optional(),
});
// ---- uploads ---------------------------------------------------------
export const InitUploadBody = z.object({
    partition: FilePartition,
    targetFolderId: z.string().uuid().nullable().optional(),
    filename: FsName,
    mimeType: z.string().min(1).max(255),
    totalSizeBytes: z.number().int().nonnegative().max(MAX_FILE_BYTES),
    /** Pre-computed MD5. When supplied AND matches an existing file's,
     *  the server returns a dedup short-circuit (UploadInitResponse
     *  kind='deduped'). When omitted, dedup is checked on `complete`. */
    expectedMd5: Md5.optional(),
    /** Negotiate chunk size — server clamps to [1 MiB, 64 MiB]. */
    chunkSize: z.number().int().min(1024).max(67_108_864).optional(),
    /** Behaviour on a same-name conflict at finalise time. Defaults to
     *  'overwrite' (single-version overwrite). 'keep-history' bumps the
     *  version_no and writes a file_versions row. 'rename' picks a
     *  unique filename suffix. 'reject' surfaces 409 FILE_NAME_TAKEN. */
    sameNameStrategy: SameNameStrategy.optional(),
    groupScopes: z.array(GroupScopeInput).max(32).optional(),
});
export const CompleteUploadBody = z.object({
    /** Optional verification — when supplied, server compares against
     *  the computed-during-write MD5 and surfaces 409 UPLOAD_MD5_MISMATCH
     *  on disagreement. When omitted, the computed MD5 is trusted. */
    expectedMd5: Md5.optional(),
    /** Optional preview metadata supplied by the client (image dimensions,
     *  pdf page count, etc.). The server stores it as-is into the file
     *  row's `preview` jsonb; tests / a future image-processor pipeline
     *  populate this. */
    preview: z.record(z.string(), z.unknown()).optional(),
});
// ---- versions --------------------------------------------------------
export const RestoreVersionBody = z.object({
    /** Optional note attached to the post-restore version row. */
    note: z.string().max(2000).nullable().optional(),
});
// ---- listing query strings -------------------------------------------
export const ListFilesQuery = z.object({
    /** Use 'root' to list files whose folderId is NULL. Omit to list all files. */
    folderId: z.union([z.string().uuid(), z.literal('root')]).optional(),
    partition: FilePartition.optional(),
    /** Substring search over filename. */
    q: z.string().max(255).optional(),
    /** Filter by mime kind prefix (e.g. 'image/' or 'application/pdf'). */
    mimePrefix: z.string().max(255).optional(),
    /** Filter by personal tag. */
    tag: z.string().max(64).optional(),
    /** When true, returns soft-deleted files (recycle bin scope). */
    recycle: z.boolean().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});
export const ListFoldersQuery = z.object({
    /** When set, lists children of this folder. When omitted with
     *  `partition`, returns top-level folders of that partition. */
    parentId: z.union([z.string().uuid(), z.literal('root')]).optional(),
    partition: FilePartition.optional(),
    recycle: z.boolean().optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});
//# sourceMappingURL=requests.js.map